import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Music, Users, Sparkles, ShoppingCart, Trash2, Brain, MapPin, DollarSign } from "lucide-react";

import { TieredPackage, calculateSmartPricing, PricingAdjustments } from "@/lib/packageTiers";
import { generateEnsemblePackages, EnsemblePackage } from "@/lib/aiPackageGenerator";
import { useToast } from "@/hooks/use-toast";
import { CartIncentives } from "@/components/CartIncentives";
import { motion } from "framer-motion";
import { useSessionTracking } from "@/hooks/useSessionTracking";

interface CulturalAnalysis {
  primaryCulture: string;
  recommendedInstruments: string[];
  recommendedMoments: string[];
  confidence: number;
  reasoning: string;
  customerMessage?: string;
}

interface RecommendedPackage extends EnsemblePackage {
  eventIndex: number;
  eventName: string;
  matchScore: number;
  genreAlignment: string[];
  culturalMatch?: boolean;
  instrumentMatch?: number;
  smartPricing?: PricingAdjustments;
  adjustedPrice?: number;
  recommendedInstruments?: string[];
  tier?: string;
  tierBadge?: string | null;
  bestValue?: boolean;
}

interface CartItem {
  package: RecommendedPackage;
  customized: boolean;
  swappedMusicians?: Map<string, string>;
  eventDate?: string;
  isConsecutive?: boolean;
}

interface BundleDiscount {
  itemIndices: number[];
  discountPercentage: number;
  savingsAmount: number;
  bundleType: 'consecutive' | 'same-event' | 'multi-moment';
  description: string;
}

const Shop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [eventRecommendations, setEventRecommendations] = useState<Map<number, RecommendedPackage[]>>(new Map());
  const [eventCulturalAnalysis, setEventCulturalAnalysis] = useState<Map<number, CulturalAnalysis>>(new Map());
  const [sessionId] = useState(() => sessionStorage.getItem('session_id') || '');
  const [playlistData, setPlaylistData] = useState<any>(null);
  const [venueCoordinates, setVenueCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [guestCount, setGuestCount] = useState<number>(100);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [bundleDiscounts, setBundleDiscounts] = useState<BundleDiscount[]>([]);
  const { contactData, formData, initialEventIndex } = location.state || {};

  // Session tracking
  const { trackInteraction, trackCartUpdate, saveAnalytics } = useSessionTracking({
    sessionId,
    onSave: () => console.log('Session analytics saved'),
  });

  useEffect(() => {
    if (!sessionId) {
      toast({
        title: "Session not found",
        description: "Please start from the beginning",
        variant: "destructive",
      });
      navigate('/');
      return;
    }

    loadPlaylistDataAndRecommendPackages();
  }, [sessionId]);

  const loadPlaylistDataAndRecommendPackages = async () => {
    try {
      console.log('Starting to load shop data...');
      setLoading(true);

      // Load favorite songs from database
      console.log('Loading favorite songs for session:', sessionId);
      const { data: songs, error: songsError } = await supabase
        .from('favorite_songs')
        .select('*')
        .eq('session_id', sessionId)
        .order('event_index', { ascending: true });

      console.log('Loaded songs:', songs?.length || 0, 'songs');

      if (songsError) {
        console.error('Error loading songs:', songsError);
        toast({
          title: "Error loading data",
          description: "Could not load your selections",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      if (!songs || songs.length === 0) {
        console.error('No songs found for session');
        toast({
          title: "No selections found",
          description: "Please add some songs to your taste profile first",
          variant: "destructive",
        });
        setLoading(false);
        navigate('/vibe-builder');
        return;
      }

      // Group songs by event
      const eventGroups = songs.reduce((acc: any, song: any) => {
        const eventIdx = song.event_index || 0;
        if (!acc[eventIdx]) acc[eventIdx] = [];
        acc[eventIdx].push(song);
        return acc;
      }, {});

      console.log('🎵 Songs grouped by event_index:', Object.keys(eventGroups).map(k => ({
        eventIndex: k,
        songCount: eventGroups[k].length
      })));

      // Load saved event steps from contact form drafts to know the actual number of events
      const { data: savedDraft } = await supabase
        .from('contact_form_drafts')
        .select('form_data')
        .eq('session_id', sessionId)
        .maybeSingle();

      const savedEventSteps = (savedDraft?.form_data as any)?.eventSteps;
      const actualEventCount = savedEventSteps?.length || 1;

      console.log('📋 Actual events from vibe builder:', actualEventCount);
      console.log('📦 Event groups found in songs:', Object.keys(eventGroups).length);

      // Only create eventSteps for events that actually exist (filter out any erroneous event_index values)
      const eventSteps = Object.keys(eventGroups)
        .map(idx => parseInt(idx))
        .filter(eventIndex => eventIndex < actualEventCount) // Only include valid event indices
        .map((eventIndex) => {
          const eventSongs = eventGroups[eventIndex];
          const savedStep = savedEventSteps?.[eventIndex];
          return {
            eventIndex,
            occasion: savedStep?.occasion || eventSongs[0]?.occasion || 'Event',
            vibe: savedStep?.vibe || eventSongs[0]?.vibe || '',
            vibes: savedStep?.vibes || eventSongs[0]?.vibes || [],
            selectedGenres: [...new Set(eventSongs.map((s: any) => s.genre).filter(Boolean))],
            selectedVibes: [...new Set(eventSongs.map((s: any) => s.vibe).filter(Boolean))],
            songs: eventSongs
          };
        });

      console.log('✅ Final eventSteps to show packages for:', eventSteps.length);

      setPlaylistData({ eventSteps });

      // Get venue coordinates if available (non-blocking)
      if (contactData?.venueAddress) {
        supabase.functions.invoke('geocode-venue', {
          body: { address: contactData.venueAddress }
        }).then(({ data, error }) => {
          if (!error && data?.coordinates) {
            setVenueCoordinates(data.coordinates);
          }
        }).catch(() => {
          // Silently fail - not critical
        });
      }

      // Set guest count
      if (contactData?.guestCount) {
        setGuestCount(contactData.guestCount);
      }

      // Generate packages immediately with basic data
      generatePackagesForEvents(eventSteps);

      // Temporarily disable background cultural analysis to avoid any hangs
      // loadCulturalAnalysisInBackground(eventSteps);

      setLoading(false);

    } catch (error) {
      console.error('Error in loadPlaylistDataAndRecommendPackages:', error);
      toast({
        title: "Error",
        description: "Something went wrong loading the shop",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  // Load cultural analysis in background without blocking UI
  const loadCulturalAnalysisInBackground = async (eventSteps: any[]) => {
    // Temporarily disabled to prevent any possibility of background hangs.
    // The shop will still function using generatePackagesForEvents.
    console.log('loadCulturalAnalysisInBackground skipped to keep UI responsive.');
    return;
  };

  // Generate basic packages without waiting for cultural analysis
  const generatePackagesForEvents = (eventSteps: any[]) => {
    const recommendationsMap = new Map<number, RecommendedPackage[]>();

    eventSteps.forEach((event, eventIndex) => {
      const primaryMoment = 'Cocktail Hour';
      const ensemblePackages = generateEnsemblePackages(
        primaryMoment,
        [],
        'Mixed'
      );

      const recommendations: RecommendedPackage[] = ensemblePackages.map((pkg, index) => {
        let matchScore = 100 - (pkg.ensembleSize * 2);
        
        const pricingAdjustments = calculateSmartPricing(
          pkg.basePrice,
          venueCoordinates?.[0] || 0,
          venueCoordinates?.[1] || 0,
          guestCount,
          pkg.guestCountMin,
          pkg.guestCountMax
        );

        return {
          ...pkg,
          eventIndex,
          eventName: event.occasion || `Event ${eventIndex + 1}`,
          matchScore,
          genreAlignment: [],
          smartPricing: pricingAdjustments,
          adjustedPrice: pricingAdjustments.finalPrice,
          bestValue: index === 2,
        };
      });

      recommendationsMap.set(eventIndex, recommendations);
    });

    setEventRecommendations(recommendationsMap);
  };

  // Update packages when cultural data arrives
  const updatePackagesWithCulturalData = (
    eventIndex: number, 
    culturalData: CulturalAnalysis,
    event: any
  ) => {
    const primaryMoment = culturalData.recommendedMoments?.[0] || 'Cocktail Hour';
    const ensemblePackages = generateEnsemblePackages(
      primaryMoment,
      culturalData.recommendedInstruments || [],
      culturalData.primaryCulture || 'Mixed'
    );

    const recommendations: RecommendedPackage[] = ensemblePackages.map((pkg, index) => {
      let matchScore = 100 - (pkg.ensembleSize * 2) + 10;
      const genreAlignment = [`${culturalData.primaryCulture} Culture`];
      
      const pricingAdjustments = calculateSmartPricing(
        pkg.basePrice,
        venueCoordinates?.[0] || 0,
        venueCoordinates?.[1] || 0,
        guestCount,
        pkg.guestCountMin,
        pkg.guestCountMax
      );

      return {
        ...pkg,
        eventIndex,
        eventName: event.occasion || `Event ${eventIndex + 1}`,
        matchScore,
        genreAlignment,
        culturalMatch: true,
        recommendedInstruments: culturalData.recommendedInstruments,
        smartPricing: pricingAdjustments,
        adjustedPrice: pricingAdjustments.finalPrice,
        bestValue: index === 2,
      };
    });

    setEventRecommendations(prev => {
      const updated = new Map(prev);
      updated.set(eventIndex, recommendations);
      return updated;
    });
  };

  const generateRecommendationsByEvent = async (eventSteps: any[]): Promise<Map<number, RecommendedPackage[]>> => {
    const recommendationsByEvent = new Map<number, RecommendedPackage[]>();
    const culturalAnalysisMap = new Map<number, CulturalAnalysis>();

    // Analyze all events' culture in parallel for faster loading
    console.log('Starting parallel cultural analysis for all events...');
    const analysisPromises = eventSteps.map(async (event, eventIndex) => {
      const genres = event.selectedGenres || [];
      const vibes = event.selectedVibes || [];
      const eventName = event.occasion || event.eventName || `Event ${eventIndex + 1}`;

      try {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Analysis timeout')), 10000)
        );

        const analysisPromise = supabase.functions.invoke('analyze-event-culture', {
          body: {
            occasion: event.occasion,
            vibe: event.vibe,
            genres,
            vibes,
            eventName,
            eventVibes: event.vibes || []
          }
        });

        const { data: culturalData, error: culturalError } = await Promise.race([
          analysisPromise,
          timeoutPromise
        ]) as any;

        if (culturalError) {
          console.error(`Cultural analysis error for event ${eventIndex}:`, culturalError);
          return { eventIndex, data: null };
        } else if (culturalData) {
          console.log(`Event ${eventIndex} cultural analysis complete:`, culturalData);
          return { eventIndex, data: culturalData };
        }
      } catch (error) {
        console.error(`Error analyzing event ${eventIndex}:`, error);
        return { eventIndex, data: null };
      }
      return { eventIndex, data: null };
    });

    // Wait for all analyses to complete
    const results = await Promise.all(analysisPromises);
    results.forEach(({ eventIndex, data }) => {
      if (data) {
        culturalAnalysisMap.set(eventIndex, data);
      }
    });

    setEventCulturalAnalysis(culturalAnalysisMap);

    // Generate recommendations for each event based on cultural analysis
    eventSteps.forEach((event, eventIndex) => {
      const genres = event.selectedGenres || [];
      const vibes = event.selectedVibes || [];
      const eventRecommendations: RecommendedPackage[] = [];
      const culturalAnalysis = culturalAnalysisMap.get(eventIndex);

      // Get culture-specific package moments
      const cultureMomentMap: Record<string, string[]> = {
        'Indian': ['Indian Ceremony', 'Baraat/Procession', 'Cocktail Hour', 'Dinner Service', 'Reception & Dancing'],
        'Jewish': ['Jewish Wedding Ceremony', 'Cocktail Hour', 'Dinner Service', 'Reception & Dancing'],
        'Chinese': ['Chinese Wedding Ceremony', 'Lunch Service', 'Cocktail Hour', 'Dinner Service'],
        'Western': ['Western Wedding Ceremony', 'Cocktail Hour', 'Lunch Service', 'Dinner Service', 'First Dance', 'Grand Entrance'],
        'Latin': ['Cocktail Hour', 'Dinner Service', 'Reception & Dancing', 'After Party'],
        'African': ['Cocktail Hour', 'Dinner Service', 'Reception & Dancing'],
        'Korean': ['Chinese Wedding Ceremony', 'Lunch Service', 'Dinner Service'],
        'Mixed': ['Cocktail Hour', 'Dinner Service', 'Reception & Dancing']
      };

      const relevantMoments = culturalAnalysis 
        ? culturalAnalysis.recommendedMoments
        : ['Cocktail Hour', 'Dinner Service', 'Reception & Dancing'];

      // Only use the FIRST relevant moment to generate exactly 6 packages (one per ensemble size)
      const primaryMoment = relevantMoments[0] || 'Cocktail Hour';
      
      const ensemblePackages = generateEnsemblePackages(
        primaryMoment,
        culturalAnalysis?.recommendedInstruments || [],
        culturalAnalysis?.primaryCulture || 'Mixed'
      );

      // Calculate match scores for each package
      ensemblePackages.forEach((pkg, index) => {
        // Base score starts high and decreases slightly for larger ensembles
        let matchScore = 100 - (pkg.ensembleSize * 2);
        const genreAlignment: string[] = [];

        // Boost for cultural alignment
        if (culturalAnalysis) {
          matchScore += 10;
          genreAlignment.push(`${culturalAnalysis.primaryCulture} Culture`);
        }

        // All instruments are AI-matched
        const instrumentMatch = pkg.musicians.length;
        genreAlignment.push(`${instrumentMatch} AI-Matched Instruments`);

        // Assign tier based on ensemble size
        let tier: string;
        let tierBadge: string | null = null;
        
        if (pkg.ensembleSize === 2) {
          tier = 'good';
          tierBadge = 'Good';
        } else if (pkg.ensembleSize === 3) {
          tier = 'better';
          tierBadge = 'Better';
        } else if (pkg.ensembleSize === 4) {
          tier = 'best';
          tierBadge = 'Best';
        } else if (pkg.ensembleSize === 5) {
          tier = 'premium';
          tierBadge = null; // No badge
        } else {
          tier = 'deluxe';
          tierBadge = null; // No badge
        }

        eventRecommendations.push({
          ...pkg,
          eventIndex,
          eventName: event.eventName || event.occasion || `Event ${eventIndex + 1}`,
          matchScore,
          genreAlignment: [...new Set(genreAlignment)],
          culturalMatch: true,
          instrumentMatch,
          recommendedInstruments: culturalAnalysis?.recommendedInstruments || [],
          tier,
          tierBadge,
          bestValue: false, // Will be updated below
        });
      });

      // Calculate price-per-musician and mark the best value package
      if (eventRecommendations.length > 0) {
        const packagesWithRatio = eventRecommendations.map((pkg, idx) => ({
          index: idx,
          pricePerMusician: pkg.basePrice / pkg.ensembleSize
        }));

        const bestValueIndex = packagesWithRatio.reduce((bestIdx, pkg) => 
          pkg.pricePerMusician < packagesWithRatio[bestIdx].pricePerMusician ? pkg.index : bestIdx
        , 0);

        eventRecommendations[bestValueIndex].bestValue = true;
      }

      // Filter packages with >50% match and sort by price (lowest to highest)
      let filteredRecommendations = eventRecommendations
        .filter(rec => rec.matchScore > 50)
        .sort((a, b) => {
          const priceA = a.adjustedPrice || a.basePrice;
          const priceB = b.adjustedPrice || b.basePrice;
          return priceA - priceB;
        });
      
      // Ensure minimum of 3 packages per event
      const MIN_PACKAGES = 3;
      if (filteredRecommendations.length < MIN_PACKAGES) {
        console.log(`Event ${eventIndex}: Only ${filteredRecommendations.length} packages with score >50, adding more...`);
        
        // Get additional packages sorted by score
        const additionalPackages = eventRecommendations
          .filter(rec => !filteredRecommendations.includes(rec))
          .sort((a, b) => b.matchScore - a.matchScore)
          .slice(0, MIN_PACKAGES - filteredRecommendations.length);
        
        filteredRecommendations = [...filteredRecommendations, ...additionalPackages];
        
        // If still not enough, add top-rated packages from any moment
        if (filteredRecommendations.length < MIN_PACKAGES) {
          console.log(`Event ${eventIndex}: Still need ${MIN_PACKAGES - filteredRecommendations.length} more packages, adding popular ones...`);
          
          const popularMoments = ['Cocktail Hour', 'Dinner Service', 'Reception & Dancing'];
          popularMoments.forEach(moment => {
            if (filteredRecommendations.length >= MIN_PACKAGES) return;
            
            // Generate fallback packages using AI generator
            const fallbackPackages = generateEnsemblePackages(moment, [], 'Mixed');
            fallbackPackages.slice(0, MIN_PACKAGES - filteredRecommendations.length).forEach(pkg => {
              filteredRecommendations.push({
                ...pkg,
                eventIndex,
                eventName: event.eventName || event.occasion || `Event ${eventIndex + 1}`,
                matchScore: 25, // Low but acceptable fallback score
                genreAlignment: ['Popular Choice'],
                culturalMatch: false,
                instrumentMatch: 0,
                recommendedInstruments: [],
                tier: pkg.ensembleSize === 2 ? 'good' : pkg.ensembleSize === 3 ? 'better' : 'best',
              });
            });
          });
        }
      }
      
      // Apply smart pricing if we have venue coordinates
      const finalRecommendations = filteredRecommendations.map(rec => {
        if (venueCoordinates) {
          const smartPricing = calculateSmartPricing(
            rec.basePrice,
            venueCoordinates.lat,
            venueCoordinates.lng,
            guestCount,
            rec.guestCountMin,
            rec.guestCountMax
          );
          return {
            ...rec,
            smartPricing,
            adjustedPrice: smartPricing.finalPrice
          };
        }
        return rec;
      });
      
      // Sort again after pricing adjustments
      finalRecommendations.sort((a, b) => {
        const priceA = a.adjustedPrice || a.basePrice;
        const priceB = b.adjustedPrice || b.basePrice;
        return priceA - priceB;
      });
      
      recommendationsByEvent.set(eventIndex, finalRecommendations);
    });

    return recommendationsByEvent;
  };

  const addToCart = (pkg: RecommendedPackage) => {
    const existingEventPackageIndex = cart.findIndex(item => item.package.eventIndex === pkg.eventIndex);

    const runTracking = (updatedCart: CartItem[], action: 'package_switched' | 'add_to_cart', extra?: any) => {
      try {
        trackInteraction('click', 'ensemble_package', {
          ensembleSize: pkg.ensembleSize,
          packageName: pkg.name,
          price: pkg.adjustedPrice || pkg.basePrice,
          eventIndex: pkg.eventIndex,
        });

        void trackCartUpdate(updatedCart.map(item => ({
          packageName: item.package.name,
          eventIndex: item.package.eventIndex,
          eventName: item.package.eventName,
          price: item.package.adjustedPrice || item.package.basePrice,
          customized: item.customized,
          musicians: item.package.musicians,
          moment: item.package.moment,
          ensembleSize: item.package.ensembleSize,
        }))).catch((err) => {
          console.error('Error tracking cart update:', err);
        });

        trackInteraction('cart_action', action, extra || { packageName: pkg.name });
      } catch (err) {
        console.error('Error in analytics tracking:', err);
      }
    };

    // If package already exists for this event, replace it
    if (existingEventPackageIndex >= 0) {
      const existingPackage = cart[existingEventPackageIndex].package;
      const newCart = [...cart];
      newCart[existingEventPackageIndex] = { package: pkg, customized: false };
      setCart(newCart);
      calculateBundleDiscounts(newCart);

      runTracking(newCart, 'package_switched', { from: existingPackage.name, to: pkg.name });

      toast({
        title: "Package switched",
        description: `Switched from "${existingPackage.name}" to "${pkg.name}" for ${pkg.eventName}`,
      });
      return;
    }

    // Add new package
    const newCart = [...cart, { package: pkg, customized: false }];
    setCart(newCart);
    calculateBundleDiscounts(newCart);

    runTracking(newCart, 'add_to_cart');

    toast({
      title: "Added to cart",
      description: `${pkg.name} for ${pkg.eventName}`,
    });
  };
  const removeFromCart = (index: number) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    calculateBundleDiscounts(newCart);
    
    try {
      void trackCartUpdate(newCart.map(item => ({
        packageName: item.package.name,
        eventIndex: item.package.eventIndex,
        eventName: item.package.eventName,
        price: item.package.adjustedPrice || item.package.basePrice,
        customized: item.customized,
        musicians: item.package.musicians,
        moment: item.package.moment,
        ensembleSize: item.package.ensembleSize,
      }))).catch((err) => {
        console.error('Error tracking cart update on remove:', err);
      });

      trackInteraction('cart_action', 'remove_from_cart');
    } catch (err) {
      console.error('Error in analytics tracking on remove:', err);
    }
    
    toast({
      title: "Removed from cart",
      description: "Package removed successfully",
    });
  };
  // Calculate bundle discounts for multi-date/multi-moment packages
  const calculateBundleDiscounts = (cartItems: CartItem[]) => {
    const discounts: BundleDiscount[] = [];
    
    // Group items by event
    const eventGroups = new Map<number, number[]>();
    cartItems.forEach((item, index) => {
      const eventIndex = item.package.eventIndex;
      if (!eventGroups.has(eventIndex)) {
        eventGroups.set(eventIndex, []);
      }
      eventGroups.get(eventIndex)!.push(index);
    });

    // Check for multi-moment bundles (multiple packages for same event)
    eventGroups.forEach((indices, eventIndex) => {
      if (indices.length >= 2) {
        const packages = indices.map(i => cartItems[i].package);
        const totalPrice = packages.reduce((sum, pkg) => sum + (pkg.adjustedPrice || pkg.basePrice), 0);
        
        // 10% discount for 2-3 moments, 15% for 4+ moments
        const discountPercentage = indices.length >= 4 ? 0.15 : 0.10;
        const savingsAmount = Math.ceil(totalPrice * discountPercentage);
        
        discounts.push({
          itemIndices: indices,
          discountPercentage: discountPercentage * 100,
          savingsAmount,
          bundleType: 'multi-moment',
          description: `${indices.length} moments for ${packages[0].eventName}`
        });
      }
    });

    // Check for consecutive event dates (if we have multiple events)
    if (eventGroups.size >= 2) {
      const allIndices = Array.from({ length: cartItems.length }, (_, i) => i);
      const totalPrice = cartItems.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0);
      
      // 12% discount for multi-event bookings
      const discountPercentage = 0.12;
      const savingsAmount = Math.ceil(totalPrice * discountPercentage);
      
      discounts.push({
        itemIndices: allIndices,
        discountPercentage: discountPercentage * 100,
        savingsAmount,
        bundleType: 'consecutive',
        description: `${eventGroups.size} events booked together`
      });
    }

    setBundleDiscounts(discounts);
  };

  const calculateTotal = (): number => {
    const subtotal = cart.reduce((total, item) => {
      return total + (item.package.adjustedPrice || item.package.basePrice);
    }, 0);

    // Apply largest bundle discount
    const maxBundleDiscount = bundleDiscounts.reduce((max, discount) => 
      Math.max(max, discount.savingsAmount), 0
    );

    return Math.max(0, subtotal - maxBundleDiscount);
  };

  const getTotalSavings = (): number => {
    const bundleSavings = bundleDiscounts.reduce((max, discount) => 
      Math.max(max, discount.savingsAmount), 0
    );

    return bundleSavings;
  };

  const handleBackNavigation = () => {
    // If we have contact data, send the user back into the taste profile flow
    if (contactData && formData) {
      const eventIndices = Array.from(eventRecommendations.keys());
      const lastEventIndex = eventIndices.length
        ? Math.max(...eventIndices)
        : (typeof initialEventIndex === 'number' ? initialEventIndex : 0);

      navigate('/vibe-builder', {
        state: {
          contactData,
          formData,
          initialEventIndex: lastEventIndex,
        },
      });
      return;
    }

    // Fallback: go back in history
    navigate(-1);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast({
        title: "Cart is empty",
        description: "Please add packages to continue",
        variant: "destructive",
      });
      return;
    }

    // 🎉 Celebration for clicking Submit!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6'],
    });

    try {
      console.log('🚀 Starting inquiry submission...');
      console.log('📦 Cart items:', cart.length);
      console.log('🎵 Session ID:', sessionId);

      // Show loading toast
      toast({
        title: "Submitting inquiry...",
        description: "Sending your event details",
      });

      // Load songs from database for this session
      console.log('🎶 Fetching songs from database...');
      const { data: songs, error: songsError } = await supabase
        .from('favorite_songs')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (songsError) {
        console.warn('⚠️ Error fetching songs for inquiry, continuing without songs:', songsError);
      }

      const safeSongs = (songs || []).map(song => ({
        song_title: song.song_title,
        artist: song.artist,
        album: song.album,
        spotify_uri: song.spotify_uri,
        event_index: song.event_index,
      }));
      console.log('✅ Loaded songs for inquiry (slimmed payload):', safeSongs.length);

      // Extract event information from playlistData and formData
      const eventDates = formData?.eventDates || (contactData?.event_date ? [{
        date: contactData.event_date,
        startTime: contactData.event_start_time,
        endTime: contactData.event_end_time,
        loadInTime: contactData.load_in_time,
      }] : []);

      const allVibes = playlistData?.eventSteps?.flatMap((step: any) => step.vibes || []) || [];
      const allGenres = playlistData?.eventSteps?.flatMap((step: any) => step.selectedGenres || []) || [];
      const occasions = playlistData?.eventSteps?.map((step: any) => step.occasion).filter(Boolean) || [];

      // Check for imported playlists in session storage
      let importedPlaylists: any[] = [];
      try {
        const spotifyData = sessionStorage.getItem(`spotify_import_${sessionId}`);
        if (spotifyData) {
          const parsed = JSON.parse(spotifyData);
          if (parsed.playlists) {
            importedPlaylists = parsed.playlists.map((p: any) => ({
              name: p.name,
              url: p.external_urls?.spotify || p.url,
              trackCount: p.tracks?.total || p.trackCount || 0,
            }));
          }
        }
      } catch (e) {
        console.warn('Could not parse imported playlists:', e);
      }

      // Prepare inquiry data with comprehensive event information
      const inquiryData = {
        packages: cart.map(item => ({
          name: item.package.name,
          price: item.package.adjustedPrice || item.package.basePrice,
          musicians: item.package.musicians.join(', '),
          description: item.package.description,
          eventName: item.package.eventName,
          moment: item.package.moment,
          instrumentMatch: item.package.instrumentMatch,
          culturalMatch: item.package.culturalMatch,
        })),
        songs: safeSongs,
        culturalAnalysis: Array.from(eventCulturalAnalysis.values()).map(analysis => ({
          primaryCulture: analysis.primaryCulture,
          recommendedInstruments: analysis.recommendedInstruments,
          recommendedMoments: analysis.recommendedMoments,
          confidence: analysis.confidence,
          reasoning: analysis.reasoning,
        })),
        sessionId,
        contactInfo: contactData,
        totalCost: calculateTotal(),
        totalSavings: getTotalSavings(),
        eventInfo: {
          dates: eventDates,
          occasion: occasions.length > 0 ? occasions.join(', ') : undefined,
          vibes: [...new Set(allVibes)],
          genres: [...new Set(allGenres)],
          importedPlaylists: importedPlaylists.length > 0 ? importedPlaylists : undefined,
        },
      };

      console.log('📧 Sending email with inquiry data...');
      console.log('📊 Data summary:', {
        packages: inquiryData.packages.length,
        songs: inquiryData.songs.length,
        culturalAnalysis: inquiryData.culturalAnalysis.length,
        totalCost: inquiryData.totalCost,
      });

      // Save submission to database first
      console.log('💾 Saving submission to database...');
      
      const submissionData = {
        name: contactData?.name || '',
        email: contactData?.email || '',
        phone: contactData?.phone || '',
        venueAddress: contactData?.venue_address || '',
        eventDate: eventDates[0]?.date || contactData?.event_date || new Date().toISOString(),
        eventStartTime: eventDates[0]?.startTime || contactData?.event_start_time || '18:00',
        eventEndTime: eventDates[0]?.endTime || contactData?.event_end_time || '23:00',
        loadInTime: eventDates[0]?.loadInTime || contactData?.load_in_time || null,
        userTimezone: contactData?.user_timezone || 'America/Chicago',
        eventDatesJson: eventDates.length > 0 ? eventDates : null,
        notes: `Packages: ${cart.map(item => item.package.name).join(', ')}. Total: $${calculateTotal().toLocaleString()}`,
      };

      const { data: submissionResponse, error: submissionError } = await supabase.functions.invoke('submit-contact', {
        body: submissionData,
      });

      if (submissionError) {
        console.error('❌ Failed to save submission:', submissionError);
        throw new Error(`Failed to save submission: ${submissionError.message || JSON.stringify(submissionError)}`);
      }

      console.log('✅ Submission saved to database:', submissionResponse);

      // Call edge function to send email
      console.log('🔗 Invoking send-inquiry-email edge function...');
      const { data: emailResponse, error: emailError } = await supabase.functions.invoke('send-inquiry-email', {
        body: inquiryData,
      });

      console.log('📬 Edge function response:', { emailResponse, emailError });

      if (emailError) {
        console.error('❌ Email function error:', emailError);
        console.error('❌ Full error details:', JSON.stringify(emailError, null, 2));
        throw new Error(`Failed to send email: ${emailError.message || JSON.stringify(emailError)}`);
      }

      if (!emailResponse || emailResponse.error) {
        console.error('❌ Email response contains error:', emailResponse);
        throw new Error(`Email send failed: ${emailResponse?.error || 'Unknown error'}`);
      }

      console.log('✅ Email sent successfully:', emailResponse);

      // 🎉 BIG celebration for successful submission!
      confetti({
        particleCount: 150,
        spread: 180,
        origin: { y: 0.6 },
        colors: ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6'],
        startVelocity: 45,
        gravity: 1,
        scalar: 1.5,
      });
      
      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#ec4899', '#f472b6', '#fbcfe8'],
        });
        confetti({
          particleCount: 100,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#8b5cf6', '#a78bfa', '#c4b5fd'],
        });
      }, 250);

      // Show success toast
      toast({
        title: "Inquiry submitted successfully!",
        description: "We'll get back to you soon with a personalized quote.",
      });

      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);

    } catch (error: any) {
      console.error('❌ Critical error submitting inquiry:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
      });
      
      toast({
        title: "Submission failed",
        description: error?.message || "Please try again or contact us directly",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Curating perfect packages for your events...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
          <div className="container max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleBackNavigation}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">Custom Package Shop</h1>
            
            {/* Cart Button */}
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="gap-2 relative">
                  <ShoppingCart className="h-4 w-4" />
                  Cart
                  {cart.length > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                      {cart.length}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Shopping Cart</SheetTitle>
                  <SheetDescription>
                    {cart.length} package(s) selected
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-8 space-y-4">
                  {cart.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Your cart is empty</p>
                    </div>
                  ) : (
                    <>
                      {cart.map((item, index) => (
                        <Card key={index} className="mb-3">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <CardTitle className="text-base">{item.package.name}</CardTitle>
                                  {bundleDiscounts.some(d => d.itemIndices.includes(index)) && (
                                    <Badge variant="default" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white animate-pulse">
                                      Bundle Savings!
                                    </Badge>
                                  )}
                                </div>
                                <CardDescription className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline" className="text-xs">
                                    {item.package.moment}
                                  </Badge>
                                  {item.package.eventName}
                                </CardDescription>
                                {item.customized && (
                                  <Badge variant="secondary" className="text-xs mt-1">
                                    Customized
                                  </Badge>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeFromCart(index)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {/* Musicians List */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <Users className="h-3 w-3 text-primary" />
                                <span>Musicians ({item.package.musicians.length})</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {item.package.musicians.map((musician, idx) => {
                                  const isRecommended = item.package.recommendedInstruments?.some(rec => 
                                    musician.toLowerCase().includes(rec.toLowerCase()) || 
                                    rec.toLowerCase().includes(musician.toLowerCase())
                                  );
                                  return (
                                    <Badge 
                                      key={idx} 
                                      variant={isRecommended ? "default" : "secondary"} 
                                      className={`text-xs ${isRecommended ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/50' : ''}`}
                                    >
                                      {musician}
                                      {isRecommended && <span className="ml-1">✨</span>}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}

                      {/* Bundle Discount Alerts */}
                      {bundleDiscounts.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {bundleDiscounts.map((discount, idx) => (
                            <Card key={idx} className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800">
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0">
                                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg px-3 py-1">
                                      🎉 {discount.discountPercentage}% OFF
                                    </Badge>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-sm mb-1">
                                      {discount.bundleType === 'multi-moment' ? '🎭 Multi-Moment Bundle' : 
                                       discount.bundleType === 'consecutive' ? '📅 Multi-Event Bundle' : 
                                       '🎪 Special Bundle'}
                                    </h4>
                                    <p className="text-xs text-muted-foreground mb-2">
                                      {discount.description}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                        Save ${discount.savingsAmount.toLocaleString()}
                                      </span>
                                      <Badge variant="secondary" className="text-xs">
                                        Best Value
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}

                      {/* Dynamic Cart Incentives */}
                      {cart.length > 0 && (
                        <div className="mb-4">
                          <CartIncentives
                            packageCount={cart.length}
                            totalAmount={cart.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0)}
                            eventCount={new Set(cart.map(item => item.package.eventIndex)).size}
                          />
                        </div>
                      )}

                      <Separator className="my-4" />

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Subtotal ({cart.length} packages)</span>
                          <span>${cart.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0).toLocaleString()}</span>
                        </div>
                        
                        {bundleDiscounts.length > 0 && (
                          <div className="flex justify-between text-sm text-green-600 dark:text-green-400 font-medium">
                            <span>Bundle Savings</span>
                            <span>-${bundleDiscounts.reduce((max, d) => Math.max(max, d.savingsAmount), 0).toLocaleString()}</span>
                          </div>
                        )}
                        
                        {getTotalSavings() > 0 && (
                          <div className="p-2 bg-green-50 dark:bg-green-950/20 rounded-lg">
                            <p className="text-xs text-green-700 dark:text-green-300 text-center font-medium">
                              🎉 Total Savings: ${getTotalSavings().toLocaleString()}
                            </p>
                          </div>
                        )}
                        
                        <Separator />
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total</span>
                          <div className="text-right">
                            {getTotalSavings() > 0 && (
                              <div className="text-xs text-muted-foreground line-through font-normal">
                                ${cart.reduce((sum, item) => sum + (item.package.adjustedPrice || item.package.basePrice), 0).toLocaleString()}
                              </div>
                            )}
                            <span className="text-primary">${calculateTotal().toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <Button 
                        className="w-full mt-6" 
                        size="lg"
                        onClick={handleCheckout}
                      >
                        Submit Inquiry
                        {getTotalSavings() > 0 && (
                          <Badge className="ml-2 bg-white text-primary">
                            Save ${getTotalSavings().toLocaleString()}
                          </Badge>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Hero Section */}
        <div className="container max-w-7xl mx-auto px-4 py-8">
          {/* Trust Bar */}
          <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border border-primary/10 rounded-lg p-4 mb-8">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium">130+ Professional Musicians</span>
              </div>
              <div className="h-4 w-px bg-border hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="font-medium">1000+ Events Served</span>
              </div>
              <div className="h-4 w-px bg-border hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏆</span>
                <span className="font-medium">Fully Insured & Licensed</span>
              </div>
              <div className="h-4 w-px bg-border hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">💯</span>
                <span className="font-medium">100% Money-Back Guarantee</span>
              </div>
            </div>
          </div>

          <div className="text-center mb-8 space-y-3">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <Badge variant="secondary" className="text-sm">
                AI-Matched To Your Taste
              </Badge>
            </div>
            <h2 className="text-3xl font-bold tracking-tight">
              Your Perfect Musicians, Ready To Book
            </h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Packages curated from your music preferences. One package per event. Price includes everything.
            </p>
          </div>

          {/* Event-Based Recommendations */}
          {Array.from(eventRecommendations.entries()).map(([eventIndex, packages]) => {
            const culturalAnalysis = eventCulturalAnalysis.get(eventIndex);
            
            return (
              <div key={eventIndex} className="mb-16">
                {/* Event Header with Cultural Analysis */}
                <div className="mb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold">
                          {packages[0]?.eventName || `Event ${eventIndex + 1}`}
                        </h3>
                        {culturalAnalysis && (
                          <Badge variant="outline" className="gap-2">
                            <Brain className="h-3 w-3" />
                            {culturalAnalysis.primaryCulture}
                          </Badge>
                        )}
                        {(() => {
                          const selectedPackage = cart.find(item => item.package.eventIndex === eventIndex);
                          return selectedPackage ? (
                            <Badge className="bg-green-600 hover:bg-green-600">
                              ✓ Selected
                            </Badge>
                          ) : null;
                        })()}
                      </div>
                      <p className="text-muted-foreground">
                        {packages.length} option{packages.length === 1 ? '' : 's'} • From ${Math.min(...packages.map(p => p.adjustedPrice || p.basePrice)).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Cultural Analysis Card */}
                  {culturalAnalysis && (
                    <Card className="bg-secondary/20 border-primary/20">
                      <CardContent className="pt-6">
                        {culturalAnalysis.customerMessage && (
                          <p className="text-sm text-foreground/90 leading-relaxed mb-4 pb-4 border-b border-border/20">
                            {culturalAnalysis.customerMessage}
                          </p>
                        )}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-semibold mb-2 flex items-center gap-2">
                              <Brain className="h-4 w-4 text-primary" />
                              Recommended Instruments
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {culturalAnalysis.recommendedInstruments.map((instrument, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {instrument}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2">Perfect Moments</h4>
                            <div className="flex flex-wrap gap-1">
                              {culturalAnalysis.recommendedMoments.map((moment, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {moment}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Package Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {packages.map((pkg, pkgIndex) => {
                    const eventHasPackage = cart.find(item => item.package.eventIndex === pkg.eventIndex);
                    const isThisPackageSelected = eventHasPackage?.package.name === pkg.name;
                    
                    return (
                      <Card 
                        key={`${eventIndex}-${pkgIndex}`}
                        className={`relative flex flex-col transition-all ${
                          isThisPackageSelected ? 'ring-2 ring-green-500 shadow-lg' : 
                          'hover:shadow-lg hover:scale-[1.02]'
                        }`}
                      >
                        {/* Badges */}
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                          {/* Best Value badge takes priority */}
                          {pkg.bestValue && (
                            <Badge className="bg-gradient-to-r from-emerald-500 to-green-500 text-white px-4 py-1 shadow-lg">
                              <DollarSign className="h-3 w-3 mr-1" />
                              Best Value
                            </Badge>
                          )}
                          {/* Perfect Match badge shows when not best value */}
                          {!pkg.bestValue && pkgIndex === 0 && (
                            <Badge className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 py-1 shadow-lg">
                              <Brain className="h-3 w-3 mr-1" />
                              Perfect Match
                            </Badge>
                          )}
                        </div>
                        
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="text-xs">
                              {pkg.ensembleName?.toUpperCase() || 'ENSEMBLE'}
                            </Badge>
                            {!pkg.bestValue && pkg.tierBadge && (
                              <Badge variant="secondary" className="text-xs">
                                {pkg.tierBadge}
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg">{pkg.name}</CardTitle>
                          <CardDescription className="text-sm line-clamp-2">{pkg.description}</CardDescription>
                        </CardHeader>
                        
                        <CardContent className="flex-1 space-y-4">
                          {/* Musicians Count */}
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="h-4 w-4 text-primary" />
                            <span className="font-medium">{pkg.musicians.length} Professional Musicians</span>
                          </div>

                          {/* Key Features - Max 3 */}
                          <div className="space-y-1">
                            {pkg.features.slice(0, 3).map((feature, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 flex-shrink-0" />
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>

                        <CardFooter className="flex flex-col gap-3 pt-4 border-t">
                          {/* Pricing */}
                          <div className="w-full">
                            <div className="text-center mb-3">
                              {pkg.adjustedPrice && pkg.adjustedPrice !== pkg.basePrice ? (
                                <>
                                  <div className="text-3xl font-bold text-primary">
                                    ${pkg.adjustedPrice.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-muted-foreground line-through">
                                    was ${pkg.basePrice.toLocaleString()}
                                  </div>
                                </>
                              ) : (
                                <div className="text-3xl font-bold">
                                  ${pkg.basePrice.toLocaleString()}
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                All-inclusive pricing
                              </p>
                            </div>
                          </div>

                          {/* CTA Button */}
                          {eventHasPackage && !isThisPackageSelected && (
                            <div className="w-full p-2 bg-blue-50 dark:bg-blue-950/20 rounded text-center border border-blue-200 dark:border-blue-800">
                              <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
                                Currently selected: "{eventHasPackage.package.name}"
                              </p>
                            </div>
                          )}
                          
                          <Button 
                            className="w-full h-12 text-base font-semibold"
                            onClick={() => addToCart(pkg)}
                            variant={isThisPackageSelected ? "secondary" : "default"}
                            size="lg"
                          >
                            {isThisPackageSelected ? (
                              <>✓ Selected for Event {eventIndex + 1}</>
                            ) : eventHasPackage ? (
                              <>Switch to This Package</>
                            ) : (
                              <>Select This Package</>
                            )}
                          </Button>
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
                
                {/* Trust Footer */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Free Consultation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Flexible Cancellation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Professional Insurance</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>✓</span>
                      <span>Regional Manager Included</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {eventRecommendations.size === 0 && (
            <Card className="text-center py-12">
              <CardContent>
                <Music className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  No recommendations found. Please complete your vibe builder.
                </p>
                <Button
                  onClick={() => navigate('/vibe-builder')}
                  className="mt-4"
                >
                  Build Your Profile
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Additional Services */}
        </div>
      </div>
    </>
  );
};

export default Shop;
